import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CellPosition, ColumnKey, Week } from "../types/workout-types";
import { ExerciseTable } from "./ExerciseTable";

interface DayTabsProps {
  currentWeek: Week;
  activeCell: CellPosition | null;
  editMode: boolean;
  tempValue: string;
  forceRender: number;
  currentDayIndex: number;
  setCurrentDayIndex: (index: number) => void;
  columns: ColumnKey[];
  setHasEdited: (edited: boolean) => void;
  finishEditingWithSave: (id: string, field: ColumnKey, tempValue: string) => void;
  setActiveCell: (cell: CellPosition | null) => void;
  startEditing: (rowIndex: number, colIndex: number, value: string) => void;
  handleCellClick: (rowIndex: number, colIndex: number, value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => void;
  inputRefs: React.MutableRefObject<Record<string, HTMLInputElement | null>>;
  handleNotesChange: (notes: string) => void;
  setForceRender?: (value: number | ((prevValue: number) => number)) => void;
  setTempValue: (value: string) => void;
}

export function DayTabs({
  currentWeek,
  activeCell,
  editMode,
  tempValue,
  forceRender,
  currentDayIndex,
  setCurrentDayIndex,
  columns,
  setHasEdited,
  finishEditingWithSave,
  setActiveCell,
  startEditing,
  handleCellClick,
  handleKeyDown,
  inputRefs,
  handleNotesChange,
  setForceRender,
  setTempValue,
}: DayTabsProps) {
  const getDynamicGridCols = () => {
    const dayCount = currentWeek.days.length;
    
    if (dayCount <= 2) return "grid-cols-2";
    if (dayCount <= 3) return "grid-cols-3";
    if (dayCount <= 4) return "grid-cols-4";
    if (dayCount <= 5) return "grid-cols-5";
    if (dayCount <= 6) return "grid-cols-6";
    return "grid-cols-7";
  };

  return (
    <div className="mt-6">
      <Tabs
        defaultValue="0"
        value={currentDayIndex.toString()}
        onValueChange={(value) => setCurrentDayIndex(parseInt(value))}
        className="relative"
      >
        <TabsList className={`grid ${getDynamicGridCols()} mb-4 bg-white/5`}>
          {currentWeek.days.map((day, index) => (
            <TabsTrigger
              key={day.id}
              value={index.toString()}
              className="text-white data-[state=active]:bg-white/10"
            >
              Day {day.day_number || index + 1}
            </TabsTrigger>
          ))}
        </TabsList>

        {currentWeek.days.map((day, dayIndex) => (
          <TabsContent
            key={day.id}
            value={dayIndex.toString()}
            className="space-y-6 outline-none border border-white/10 rounded-md p-6 bg-white/5"
            tabIndex={-1}
          >
            <div className="space-y-2">
              <div className="text-xl font-bold text-white">
                {day.focus || day.title}
              </div>
              {day.subtitle && (
                <div className="text-sm text-white/70">
                  {day.subtitle}
                </div>
              )}
              {day.session_type && (
                <div className="text-sm text-white/70">
                  Session Type: {day.session_type}
                </div>
              )}
            </div>

            <ExerciseTable
              day={day}
              activeCell={activeCell}
              editMode={editMode}
              tempValue={tempValue}
              currentDayIndex={currentDayIndex}
              dayIndex={dayIndex}
              columns={columns}
              setHasEdited={setHasEdited}
              finishEditingWithSave={finishEditingWithSave}
              setActiveCell={setActiveCell}
              startEditing={startEditing}
              handleCellClick={handleCellClick}
              handleKeyDown={handleKeyDown}
              inputRefs={inputRefs}
              handleNotesChange={handleNotesChange}
              setForceRender={setForceRender}
              setTempValue={setTempValue}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
